import { useState, useEffect, useRef } from "react";
import { UserCircle, Upload } from "lucide-react";
import { Modal, FormField, Input, Button } from "../ui/index";
import PhoneInput from "../ui/PhoneInput";
import AddressAutocomplete from "../ui/AddressAutocomplete";
import { cascadeAPI } from "../../api/services";

export default function EditClientModal({ isOpen, onClose, onEdit, item }) {
  const [form, setForm] = useState({ nom: "", revendeur: "", email: "", adresse: "", type: "Entreprise", responsable: "" });
  const [telephones, setTelephones] = useState([""]);
  const [photo, setPhoto] = useState(null);
  const [revendeurs, setRevendeurs] = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    if (isOpen) {
      cascadeAPI.revendeurs().then(res => setRevendeurs(res.data.data)).catch(() => {});
    }
  }, [isOpen]);

  useEffect(() => {
    if (item) {
      setForm({
        nom: item.nom || "",
        revendeur: item.revendeur?._id || item.revendeur || "",
        email: item.email || "",
        adresse: item.adresse || "",
        type: item.type || "Entreprise",
        responsable: item.responsable || "",
      });
      setTelephones(item.telephones?.length ? [...item.telephones] : [""]);
      setPhoto(item.photo || null);
    }
  }, [item]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const handlePhoto = (e) => { const f = e.target.files[0]; if (f) setPhoto(URL.createObjectURL(f)); };

  const handleSubmit = () => {
    if (!form.nom || !form.revendeur || !form.email) return;
    onEdit?.({ ...form, telephones, photo });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le client" subtitle="Modifiez les informations du client.">
      <div className="space-y-4">

        {/* Photo profil */}
        <FormField label="Photo de profil">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200">
              {photo
                ? <img src={photo} alt="profil" className="w-full h-full object-cover" />
                : <UserCircle size={32} className="text-slate-400" />
              }
            </div>
            <button
              onClick={() => fileRef.current.click()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50 transition-colors"
            >
              <Upload size={16} className="text-slate-400" /> Télécharger une photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          </div>
        </FormField>

        {/* Nom + Revendeur */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Nom du client" required>
            <Input value={form.nom} onChange={handleChange("nom")} placeholder="Nom du client" />
          </FormField>
          <FormField label="Revendeur" required>
            <select
              value={form.revendeur}
              onChange={handleChange("revendeur")}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner un revendeur</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          </FormField>
        </div>

        {/* Email */}
        <FormField label="Email" required>
          <Input value={form.email} onChange={handleChange("email")} placeholder="exemple@gmail.com" type="email" />
        </FormField>

        {/* Téléphones */}
        <FormField label="Téléphone(s)" required>
          <PhoneInput value={telephones} onChange={setTelephones} />
        </FormField>

        {/* Adresse */}
        <FormField label="Adresse complète">
          <AddressAutocomplete
            value={form.adresse}
            onChange={(val) => setForm(f => ({ ...f, adresse: val }))}
            onSelect={({ address }) => setForm(f => ({ ...f, adresse: address }))}
            placeholder="Rechercher une adresse..."
          />
        </FormField>

        {/* Type */}
        <FormField label="Type" required>
          <select
            value={form.type}
            onChange={handleChange("type")}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option>Entreprise</option>
            <option>Institution</option>
            <option>Particulier</option>
          </select>
        </FormField>

        {/* Responsable (si non particulier) */}
        {form.type !== "Particulier" && (
          <FormField label="Responsable">
            <Input value={form.responsable} onChange={handleChange("responsable")} placeholder="Nom du représentant" />
          </FormField>
        )}

        {/* Boutons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit}>Sauvegarder</Button>
        </div>
      </div>
    </Modal>
  );
}
